/**
 * Version Management Service
 * 
 * Handles version checking and update enforcement.
 * 
 * Features:
 * - Checks against Firestore for minimum required versions
 * - Supports both soft (recommended) and hard (forced) updates
 * - Platform-specific version checking
 * - Grace period for soft updates
 */

import { Platform } from 'react-native';
import { db, doc, getDoc } from '../config/firebase';
import AsyncStorage from '@react-native-async-storage/async-storage';
// @ts-ignore - app.json version
import appConfig from '../app.json';

export interface VersionConfig {
  // Minimum version that must be installed (hard block if below)
  minimumVersion: string;
  // Recommended version (soft prompt if below)
  recommendedVersion: string;
  // Latest available version
  latestVersion: string;
  // Update message to show users
  updateMessage?: string;
  // Force update immediately (emergency kill switch)
  forceUpdate: boolean;
  // URL to app store listing
  appStoreUrl?: string;
  playStoreUrl?: string;
}

interface PlatformVersions {
  ios: VersionConfig;
  android: VersionConfig;
  web: VersionConfig;
}

const VERSION_CHECK_KEY = 'last_version_check';
const UPDATE_DISMISSED_KEY = 'update_dismissed_at';
const VERSION_CHECK_INTERVAL = 6 * 60 * 60 * 1000; // 6 hours
const SOFT_UPDATE_GRACE_PERIOD = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Compare semantic versions (e.g., "1.2.3")
 * Returns: -1 if v1 < v2, 0 if equal, 1 if v1 > v2
 */
function compareVersions(v1: string, v2: string): number {
  const parts1 = v1.split('.').map(Number);
  const parts2 = v2.split('.').map(Number);
  
  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const part1 = parts1[i] || 0;
    const part2 = parts2[i] || 0;
    
    if (part1 < part2) return -1;
    if (part1 > part2) return 1;
  }
  
  return 0;
}

/**
 * Get current app version from app.json
 */
export function getCurrentVersion(): string {
  return appConfig.expo.version;
}

/**
 * Get platform-specific version config from Firestore
 */
async function getVersionConfig(): Promise<VersionConfig | null> {
  try {
    const docRef = doc(db, 'appConfig', 'versions');
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      console.warn('⚠️ [VersionService] No version config found in Firestore');
      return null;
    }
    
    const data = docSnap.data() as PlatformVersions;
    const platform = Platform.OS as 'ios' | 'android' | 'web';
    
    return data[platform] || null;
  } catch (error) {
    console.error('❌ [VersionService] Error fetching version config:', error);
    return null;
  }
}

/**
 * Check if we should perform a version check (rate limiting)
 */
async function shouldCheckVersion(): Promise<boolean> {
  try {
    const lastCheck = await AsyncStorage.getItem(VERSION_CHECK_KEY);
    
    if (!lastCheck) {
      return true;
    }
    
    const timeSinceCheck = Date.now() - parseInt(lastCheck, 10);
    return timeSinceCheck >= VERSION_CHECK_INTERVAL;
  } catch (error) {
    console.error('❌ [VersionService] Error checking version timestamp:', error);
    return true; // Check on error to be safe
  }
}

/**
 * Mark that we've performed a version check
 */
async function recordVersionCheck(): Promise<void> {
  try {
    await AsyncStorage.setItem(VERSION_CHECK_KEY, Date.now().toString());
  } catch (error) {
    console.error('❌ [VersionService] Error recording version check:', error);
  }
}

/**
 * Check if soft update was recently dismissed (grace period)
 */
async function isInGracePeriod(): Promise<boolean> {
  try {
    const dismissedAt = await AsyncStorage.getItem(UPDATE_DISMISSED_KEY);
    
    if (!dismissedAt) {
      return false;
    }
    
    const timeSinceDismiss = Date.now() - parseInt(dismissedAt, 10);
    return timeSinceDismiss < SOFT_UPDATE_GRACE_PERIOD;
  } catch (error) {
    console.error('❌ [VersionService] Error checking grace period:', error);
    return false;
  }
}

/**
 * Mark that user dismissed the soft update prompt
 */
export async function dismissSoftUpdate(): Promise<void> {
  try {
    await AsyncStorage.setItem(UPDATE_DISMISSED_KEY, Date.now().toString());
  } catch (error) {
    console.error('❌ [VersionService] Error dismissing update:', error);
  }
}

/**
 * Clear dismissed timestamp (when showing prompt again)
 */
export async function clearDismissal(): Promise<void> {
  try {
    await AsyncStorage.removeItem(UPDATE_DISMISSED_KEY);
  } catch (error) {
    console.error('❌ [VersionService] Error clearing dismissal:', error);
  }
}

export enum UpdateStatus {
  UP_TO_DATE = 'up_to_date',
  SOFT_UPDATE = 'soft_update', // Recommended but not required
  HARD_UPDATE = 'hard_update', // Required to continue
  EMERGENCY_UPDATE = 'emergency_update', // Force update flag set
}

export interface VersionCheckResult {
  status: UpdateStatus;
  currentVersion: string;
  minimumVersion?: string;
  recommendedVersion?: string;
  latestVersion?: string;
  updateMessage?: string;
  appStoreUrl?: string;
  playStoreUrl?: string;
  shouldShowPrompt: boolean; // Whether to show UI (respects grace period)
}

/**
 * Check if app version meets requirements
 */
export async function checkVersion(): Promise<VersionCheckResult> {
  const currentVersion = getCurrentVersion();
  
  // Rate limit checks to avoid excessive Firestore reads
  if (!(await shouldCheckVersion())) {
    console.log('⏭️ [VersionService] Skipping check (too soon since last check)');
    return {
      status: UpdateStatus.UP_TO_DATE,
      currentVersion,
      shouldShowPrompt: false,
    };
  }
  
  const config = await getVersionConfig();
  
  if (!config) {
    // No config found - assume app is okay
    await recordVersionCheck();
    return {
      status: UpdateStatus.UP_TO_DATE,
      currentVersion,
      shouldShowPrompt: false,
    };
  }
  
  await recordVersionCheck();
  
  // Emergency kill switch
  if (config.forceUpdate) {
    console.warn('🚨 [VersionService] EMERGENCY UPDATE REQUIRED');
    return {
      status: UpdateStatus.EMERGENCY_UPDATE,
      currentVersion,
      minimumVersion: config.minimumVersion,
      latestVersion: config.latestVersion,
      updateMessage: config.updateMessage || 'A critical update is required to continue.',
      appStoreUrl: config.appStoreUrl,
      playStoreUrl: config.playStoreUrl,
      shouldShowPrompt: true,
    };
  }
  
  // Check minimum version (hard requirement)
  if (compareVersions(currentVersion, config.minimumVersion) < 0) {
    console.warn('⛔ [VersionService] App version too old. Minimum:', config.minimumVersion, 'Current:', currentVersion);
    return {
      status: UpdateStatus.HARD_UPDATE,
      currentVersion,
      minimumVersion: config.minimumVersion,
      latestVersion: config.latestVersion,
      updateMessage: config.updateMessage || 'This version is no longer supported. Please update to continue.',
      appStoreUrl: config.appStoreUrl,
      playStoreUrl: config.playStoreUrl,
      shouldShowPrompt: true,
    };
  }
  
  // Check recommended version (soft prompt)
  if (compareVersions(currentVersion, config.recommendedVersion) < 0) {
    const inGracePeriod = await isInGracePeriod();
    
    console.log('📱 [VersionService] Update recommended:', {
      current: currentVersion,
      recommended: config.recommendedVersion,
      inGracePeriod,
    });
    
    return {
      status: UpdateStatus.SOFT_UPDATE,
      currentVersion,
      recommendedVersion: config.recommendedVersion,
      latestVersion: config.latestVersion,
      updateMessage: config.updateMessage || 'A new version is available with improvements and bug fixes.',
      appStoreUrl: config.appStoreUrl,
      playStoreUrl: config.playStoreUrl,
      shouldShowPrompt: !inGracePeriod, // Only show if not in grace period
    };
  }
  
  console.log('✅ [VersionService] App is up to date:', currentVersion);
  return {
    status: UpdateStatus.UP_TO_DATE,
    currentVersion,
    shouldShowPrompt: false,
  };
}

/**
 * Get the appropriate app store URL for current platform
 */
export function getAppStoreUrl(config: VersionCheckResult): string | null {
  if (Platform.OS === 'ios' && config.appStoreUrl) {
    return config.appStoreUrl;
  }
  
  if (Platform.OS === 'android' && config.playStoreUrl) {
    return config.playStoreUrl;
  }
  
  return null;
}

/**
 * Open the app store for updates
 */
export async function openAppStore(config: VersionCheckResult): Promise<void> {
  const url = getAppStoreUrl(config);
  
  if (!url) {
    console.warn('⚠️ [VersionService] No app store URL configured for this platform');
    return;
  }
  
  const { Linking } = require('react-native');
  
  try {
    const canOpen = await Linking.canOpenURL(url);
    if (canOpen) {
      await Linking.openURL(url);
    } else {
      console.error('❌ [VersionService] Cannot open URL:', url);
    }
  } catch (error) {
    console.error('❌ [VersionService] Error opening app store:', error);
  }
}

